import { OrderItemDto } from '@up/data';

/**
 * get list of modifiers for orderItem
 * @param orderItem Order item.
 * @returns modifiers list
 */
export function getOrderModifiers(item: OrderItemDto): string[] {
  const modifiers: string[] = [];

  if (item.orderItemModifiers?.length) {
    for (const itemModifier of item.orderItemModifiers) {
      const { modifier } = itemModifier;
      if (modifier.isMultiSelect) {
        const { id, name } = modifier;

        if (modifiers.find(value => value.includes(name))) continue;

        const quantity = item.orderItemModifiers.reduce(
          (prev, curr) => (curr.modifier.id === id ? ++prev : prev),
          0
        );

        modifiers.push(`${quantity}x ${name}`);
      } 
      else modifiers.push(modifier.name);
    }
  }

  return modifiers;
}

/**
 * get list of removed ingredients for order items
 * @param orderItems Order Items.
 * @returns removed ingredients list
 */
export function getOrderRemovedIngredients(orderItems: OrderItemDto): string[] {
  const { stock: ingredients } = orderItems;

  return ingredients
    ?.filter(ingredient => ingredient.isIngredient)
    ?.map(ingredient => `No ${ingredient.name}`);
}

/**
 * get Two-Dimensional list of bundles for order items
 * @param orderItems Order Items.
 * @returns Order items Two-Dimensional list
 */
export function getOrderBundles(cartProducts: OrderItemDto[]): OrderItemDto[][] {
  const bundleProducts = {};

  for (const item of cartProducts) {
    const { product: { isBundle } , bundleCombinationId } = item;

    if (isBundle || bundleCombinationId) {
      if (!bundleProducts[bundleCombinationId]) {
        bundleProducts[bundleCombinationId] = [];
      }

      if (isBundle) {
        bundleProducts[bundleCombinationId].unshift(item);
      } else {
        bundleProducts[bundleCombinationId].push(item);
      }
    }
  }

  const hasBundleDiscount = Object.values(bundleProducts)?.some((bundleProduct: OrderItemDto[]) => {
    return bundleProduct.find(product => product.product?.isBundle)
  });

  if(!hasBundleDiscount) {
    return cartProducts
      .filter((item) => item.bundleCombinationId)
      .map((item) => [item]);
  }

  return Object.values(bundleProducts);
}

/**
 * get Two-Dimensional list of single items for order Items
 * @param orderItems Order Items.
 * @returns Order items Two-Dimensional list
 */
export function getOrderSingleItems(
  cartProducts: OrderItemDto[]
): OrderItemDto[][] {
  return cartProducts
    .filter((item) => !item.product?.isBundle && !item.bundleCombinationId)
    .map((item) => [item]);
}

/**
 * get count for order items where bundles are considered as 1 entity
 * @param orderItems Order Items.
 * @returns number of items
 */
export function countOrderItems(orderItems: OrderItemDto[]): number {
  if (!orderItems || orderItems?.length == 0) return 0;

  const singleItems = getOrderSingleItems(orderItems);
  const bundleItems = getOrderBundles(orderItems);

  const count = [...singleItems, ...bundleItems]?.reduce((prev, curr) => {
    const [product] = curr;
    return prev + product.quantity; 
  }, 0);
  
  return count;
}